import React, { useState, useEffect, useRef } from "react";
import { useTranslations } from "next-intl";
import ModalLoadingOverlay from "./ModalLoadingOverlay";
import { loginUser, verifyOtp } from "../services/authService";
import { userService } from "../services/services";
import { authStorage } from "../lib/authStorage";
import { toast } from "react-toastify";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
};

export default function NewModal({ isOpen, onClose, onSuccess }: Props) {
  const t = useTranslations('loginModal');
  const [whatsapp, setWhatsapp] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState<{ whatsapp?: string, otp?: string }>({});

  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [timer, setTimer] = useState(0);
  const [isGalaxyS8Plus, setIsGalaxyS8Plus] = useState(false);
  const [isGalaxyZFold5, setIsGalaxyZFold5] = useState(false);
  const [isNestHub, setIsNestHub] = useState(false);

  const phoneInputRef = useRef<HTMLInputElement>(null);
  const firstOtpRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isOpen || otpSent) return;
    const id = window.setTimeout(() => phoneInputRef.current?.focus(), 80);
    return () => window.clearTimeout(id);
  }, [isOpen, otpSent]);

  useEffect(() => {
    if (!otpSent) return;
    const id = window.setTimeout(() => firstOtpRef.current?.focus(), 80);
    return () => window.clearTimeout(id);
  }, [otpSent]);

  useEffect(() => {
    if (typeof navigator === "undefined" || typeof window === "undefined") return;
    const userAgent = navigator.userAgent;
    setIsGalaxyS8Plus(/SM-G955|Galaxy S8\+/i.test(userAgent));

    const isFold5ByUA = /SM-F946|Galaxy Z Fold5|Galaxy Z Fold 5/i.test(userAgent);
    const viewportSignature = `${window.innerWidth}x${window.innerHeight}`;
    const screenSignature = `${window.screen.width}x${window.screen.height}`;
    const isFold5ByViewport = /344x882|882x344|345x883|883x345/i.test(viewportSignature);
    const isFold5ByScreen = /344x882|882x344|345x883|883x345/i.test(screenSignature);

    setIsGalaxyZFold5(isFold5ByUA || isFold5ByViewport || isFold5ByScreen);

    const isNestHubByUA = /Nest Hub|CrKey/i.test(userAgent);
    const isNestHubByViewport = /1024x600|600x1024/i.test(viewportSignature);
    const isNestHubByScreen = /1024x600|600x1024/i.test(screenSignature);
    setIsNestHub(isNestHubByUA || isNestHubByViewport || isNestHubByScreen);
  }, []);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [timer]);

  useEffect(() => {
    if (typeof document === "undefined") return;
    if (!isOpen) return;

    const previousBodyOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;

    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) return;
    setWhatsapp("");
    setOtp("");
    setOtpSent(false);
    setError("");
    setFieldErrors({});
    setLoading(false);
    setVerifying(false);
    setTimer(0);
  }, [isOpen]);

  if (!isOpen) return null;

  const validatePhone = (num: string) => {
    if (!num.trim()) return t('errors.required');
    if (!/^\d+$/.test(num)) return t('errors.numbersOnly');
    if (num.length !== 10) return t('errors.mustBe10');
    if (!/^[6-9]/.test(num)) {
      return t('errors.mustStartWith');
    }
    return "";
  };

  const handleSendOtp = async () => {
    const whatsappError = validatePhone(whatsapp);

    if (whatsappError) {
      setFieldErrors({ whatsapp: whatsappError });
      return;
    }

    setLoading(true);
    setError("");

    try {
      await loginUser(whatsapp);
      setOtp("");
      setOtpSent(true);
      setTimer(60);
    } catch (err: unknown) {
      if (err instanceof Error) {
        setError(err.message || "Failed to send OTP. Please try again.");
      } else {
        setError("Failed to send OTP. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (timer === 0) {
      toast.error("Invalid or expired OTP. Please try again.");
      return;
    }

    if (!otp.trim() || otp.length !== 6) {
      setFieldErrors((prev) => ({ ...prev, otp: "Please enter a valid 6-digit OTP." }));
      return;
    }

    setVerifying(true);
    setError("");

    try {
      const data = await verifyOtp(whatsapp, otp);
      if (data && data.userId) {
        authStorage.setUserId(data.userId);

        try {
          const userDetails = await userService.getUserDetails(data.userId);
          authStorage.setUserDetails(userDetails);
        } catch { }

        authStorage.notifyAuthChanged();
        onSuccess();
      } else {
        setError("Invalid response from server.");
      }
    } catch (err: unknown) {
    } finally {
      setVerifying(false);
    }
  };

  const handleResendOtp = () => {
    if (timer === 0) {
      handleSendOtp();
    }
  };

  return (
    <div className={isNestHub ? "fixed inset-0 z-100 flex items-center justify-center overflow-hidden bg-slate-900/40 p-2 backdrop-blur-sm" : "fixed inset-0 z-100 flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-sm"}>
      <div className={isNestHub ? "relative my-2 w-full max-w-md overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-2xl" : "relative w-full max-w-md overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-2xl"}>
        {(loading || verifying) && <ModalLoadingOverlay message={t('loggingIn')} />}

        <div className={isNestHub ? "flex justify-end p-2" : "flex justify-end p-4"}>
          <button
            onClick={() => {
              if (!loading && !verifying) onClose();
            }}
            disabled={loading || verifying}
            className="text-slate-400 hover:text-slate-600 disabled:cursor-not-allowed"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-5 w-5 text-gray-400"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" /></svg>
          </button>
        </div>

        <div className={isNestHub ? "px-4 pb-4 sm:px-4 sm:pb-4" : "px-6 pb-8 sm:px-8 sm:pb-10"}>

          <div className={isNestHub ? "space-y-4" : "space-y-6"}>
            <div className="text-center">
              <h2 className="text-xl font-extrabold sm:text-2xl">{t('title')}</h2>
              <p className="mt-2 text-sm text-slate-500">
                {t('subtitle')}
              </p>
            </div>

            <div className={isNestHub ? "space-y-4" : "space-y-5"}>
              <div className="space-y-2">
                <label className="text-xs font-bold text-slate-500">
                  {t('whatsappNumber')}
                </label>
                <input
                  ref={phoneInputRef}
                  type="tel"
                  placeholder={t('whatsappPlaceholder')}
                  value={whatsapp}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, "");
                    setWhatsapp(value);
                    setFieldErrors({ whatsapp: validatePhone(value) });
                  }}
                  className={`w-full rounded-xl border bg-slate-50 px-4 py-3 text-sm ${fieldErrors.whatsapp ? "border-red-500" : ""
                    }`}
                  disabled={otpSent}
                />
                {fieldErrors.whatsapp && (
                  <p className="text-xs text-red-500">{fieldErrors.whatsapp}</p>
                )}
              </div>

              <button
                onClick={handleSendOtp}
                className={isNestHub ? "w-full rounded-xl bg-blue-500 py-3 font-bold text-white shadow hover:bg-blue-600 disabled:opacity-60" : "w-full rounded-xl bg-blue-500 py-4 font-bold text-white shadow hover:bg-blue-600 disabled:opacity-60"}
                disabled={loading || otpSent}
              >
                {loading ? t('sending') : (otpSent ? "OTP Sent" : t('login'))}
              </button>

              {otpSent && (
                <div className={isNestHub ? "mt-4 space-y-3 border-t border-slate-100 pt-4" : "mt-6 space-y-5 border-t border-slate-100 pt-6"}>
                  <p className={isNestHub ? "text-xs font-medium text-green-600 text-center" : "text-sm font-medium text-green-600 text-center"}>
                    The OTP has been sent to your WhatsApp number.
                  </p>
                  <div className={isNestHub ? "flex flex-col items-center justify-center space-y-1" : "flex flex-col items-center justify-center space-y-2"}>
                    <label className={isNestHub ? "w-full text-center text-[10px] font-bold text-slate-500" : "w-full text-center text-xs font-bold text-slate-500"}>
                      Enter 6-digit OTP
                    </label>
                    <div className={isNestHub ? "mx-auto flex w-fit justify-center gap-0.5" : (isGalaxyZFold5 ? "mx-auto flex w-fit justify-center gap-1" : (isGalaxyS8Plus ? "flex w-full justify-center gap-1" : "flex justify-between w-full"))}>
                      {Array.from({ length: 6 }).map((_, index) => (
                        <input
                          key={index}
                          ref={index === 0 ? firstOtpRef : undefined}
                          type="text"
                          maxLength={1}
                          value={otp[index] || ""}
                          onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, "");

                            if (!value) {
                              setOtp((prev) => {
                                const arr = prev.split("");
                                arr[index] = "";
                                return arr.join("");
                              });
                              return;
                            }

                            const otpArray = otp.split("");
                            otpArray[index] = value;
                            const newOtp = otpArray.join("");

                            setOtp(newOtp);
                            if (fieldErrors.otp) {
                              setFieldErrors((prev) => ({ ...prev, otp: undefined }));
                            }

                            const nextInput = document.getElementById(
                              `otp-input-${index + 1}`
                            ) as HTMLInputElement;

                            if (nextInput) {
                              nextInput.focus();
                            }
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Backspace" && !e.currentTarget.value && index > 0) {
                              const prevInput = document.getElementById(
                                `otp-input-${index - 1}`
                              ) as HTMLInputElement;
                              if (prevInput) {
                                prevInput.focus();
                              }
                            }
                          }}
                          id={`otp-input-${index}`}
                          className={`${isNestHub ? "h-8 w-8 text-xs" : (isGalaxyZFold5 ? "h-10 w-10 text-base" : "h-12 w-12 text-lg")} rounded-lg border text-center font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500 ${fieldErrors.otp ? "border-red-500" : "border-slate-300"
                            }`}
                        />
                      ))}
                    </div>
                    {fieldErrors.otp && (
                      <p className="text-xs text-red-500 text-center">{fieldErrors.otp}</p>
                    )}
                  </div>

                  <button
                    onClick={handleVerifyOtp}
                    className={isNestHub ? "w-full rounded-xl bg-blue-500 py-3 font-bold text-white shadow hover:bg-blue-600 disabled:opacity-60" : "w-full rounded-xl bg-blue-500 py-4 font-bold text-white shadow hover:bg-blue-600 disabled:opacity-60"}
                    disabled={verifying || loading}
                  >
                    {verifying ? "Verifying..." : "Verify OTP"}
                  </button>

                  <div className="text-center">
                    <button
                      onClick={handleResendOtp}
                      disabled={timer > 0 || loading || verifying}
                      className="text-sm font-medium text-slate-500 hover:text-blue-500 disabled:opacity-50 disabled:hover:text-slate-500 transition-colors"
                    >
                      {timer > 0 ? `Resend OTP in ${timer}s` : "Resend OTP"}
                    </button>
                  </div>
                </div>
              )}
              {error && <div className="mt-2 text-center text-sm text-red-500">{error}</div>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
